/**
 * Convex polygon collision: separating-axis test to find the deepest face,
 * then Sutherland–Hodgman clipping to get up to two contact points.
 * Contact points are what make a stack of blocks sit still instead of jittering.
 */

import type { Body } from './body.ts';
import type { Vec } from './vec.ts';
import { dot, normalize, sub, toWorld } from './vec.ts';

export interface Manifold {
  a: Body;
  b: Body;
  /** Unit normal pointing from A towards B. */
  normal: Vec;
  depth: number;
  contacts: Vec[];
}

/** World-space vertices of a body. */
export function worldVerts(body: Body): Vec[] {
  return body.verts.map((v) => toWorld(v, body.pos, body.angle));
}

/** Outward unit normal of edge i (winding produced by convexHull, y-down screen space). */
function edgeNormal(poly: Vec[], i: number): Vec {
  const a = poly[i];
  const b = poly[(i + 1) % poly.length];
  return normalize({ x: b.y - a.y, y: -(b.x - a.x) });
}

/** Deepest penetration of B into A's faces. Positive means separated. */
function maxSeparation(polyA: Vec[], polyB: Vec[]): { sep: number; face: number } {
  let best = -Infinity;
  let bestFace = 0;
  for (let i = 0; i < polyA.length; i++) {
    const n = edgeNormal(polyA, i);
    const v = polyA[i];
    // Support point of B along -n
    let min = Infinity;
    for (const p of polyB) {
      const d = dot(n, sub(p, v));
      if (d < min) min = d;
    }
    if (min > best) {
      best = min;
      bestFace = i;
    }
  }
  return { sep: best, face: bestFace };
}

/** The face of `poly` most anti-parallel to `normal`. */
function incidentFace(poly: Vec[], normal: Vec): number {
  let best = Infinity;
  let bestFace = 0;
  for (let i = 0; i < poly.length; i++) {
    const d = dot(edgeNormal(poly, i), normal);
    if (d < best) {
      best = d;
      bestFace = i;
    }
  }
  return bestFace;
}

/** Clip a segment to the half-space dot(n, p) <= offset. */
function clipSegment(a: Vec, b: Vec, n: Vec, offset: number): Vec[] {
  const da = dot(n, a) - offset;
  const db = dot(n, b) - offset;
  const out: Vec[] = [];
  if (da <= 0) out.push(a);
  if (db <= 0) out.push(b);
  if (da * db < 0) {
    const t = da / (da - db);
    out.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
  }
  return out.slice(0, 2);
}

/** Cheap radius rejection before the expensive test. */
export function mayCollide(a: Body, b: Body): boolean {
  const dx = a.pos.x - b.pos.x;
  const dy = a.pos.y - b.pos.y;
  const r = a.radius + b.radius;
  return dx * dx + dy * dy <= r * r;
}

export function collide(a: Body, b: Body): Manifold | null {
  const pa = worldVerts(a);
  const pb = worldVerts(b);
  if (pa.length < 3 || pb.length < 3) return null;

  const sa = maxSeparation(pa, pb);
  if (sa.sep > 0) return null;
  const sb = maxSeparation(pb, pa);
  if (sb.sep > 0) return null;

  // Prefer A as reference unless B is clearly deeper (bias avoids flip-flopping).
  let refPoly = pa;
  let incPoly = pb;
  let refFace = sa.face;
  let flip = false;
  if (sb.sep > sa.sep + 0.5) {
    refPoly = pb;
    incPoly = pa;
    refFace = sb.face;
    flip = true;
  }

  const refNormal = edgeNormal(refPoly, refFace);
  const rv1 = refPoly[refFace];
  const rv2 = refPoly[(refFace + 1) % refPoly.length];
  const sideDir = normalize(sub(rv2, rv1));

  const incFace = incidentFace(incPoly, refNormal);
  let clipped = [incPoly[incFace], incPoly[(incFace + 1) % incPoly.length]];

  clipped = clipSegment(clipped[0], clipped[1], { x: -sideDir.x, y: -sideDir.y }, -dot(sideDir, rv1));
  if (clipped.length < 2) return null;
  clipped = clipSegment(clipped[0], clipped[1], sideDir, dot(sideDir, rv2));
  if (clipped.length < 2) return null;

  const refOffset = dot(refNormal, rv1);
  const contacts: Vec[] = [];
  let depth = 0;
  for (const p of clipped) {
    const sep = dot(refNormal, p) - refOffset;
    if (sep <= 0) {
      contacts.push(p);
      depth = Math.max(depth, -sep);
    }
  }
  if (contacts.length === 0) return null;

  // Normal must always point from manifold.a towards manifold.b.
  const normal = flip ? { x: -refNormal.x, y: -refNormal.y } : refNormal;
  return { a, b, normal, depth, contacts };
}
